import React from 'react';

import { TableProps } from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';

import { StringGroupItem } from 'src/generated/graphql';
import { formatEpochMillisDate } from './utils';
import { NOT_AVAILABLE_ID } from 'src/common/utils/dateFormatters';
import ExpandableCell from 'src/features/namespace/components/ExpandableCell';
import { buildStringGroupPath } from 'src/common/routes';
import { useNavigate } from 'react-router';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

interface StringGroupLinkProps {
  namespaceId: string;
  stringGroupId: string;
}

const StringGroupLink = ({
  namespaceId,
  stringGroupId,
}: StringGroupLinkProps) => {
  const navigate = useNavigate();
  // Both ids are required rather than defaulted: an empty segment yields
  // `/ns/X/sg/`, which cannot match `:stringGroupId`, so the link would render
  // as live and then fall through to the `*` catch-all on click.
  const href = buildStringGroupPath(namespaceId, stringGroupId);
  return (
    <Link
      variant="secondary"
      href={href}
      onFollow={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {stringGroupId}
    </Link>
  );
};

export const getStringGroupColumns = (
  t: TranslateFn,
  locale: string,
  namespaceId: string,
): TableProps.ColumnDefinition<StringGroupItem>[] => [
  {
    id: 'stringGroupId',
    header: t('sg-column-name'),
    sortingField: 'stringGroupId',
    width: 200,
    minWidth: 140,
    cell: (item) => (
      <StringGroupLink
        namespaceId={namespaceId}
        stringGroupId={item.stringGroupId}
      />
    ),
  },
  {
    id: 'clientId',
    header: t('sg-column-client-id'),
    width: 120,
    minWidth: 80,
    cell: (item) => item.clientId ?? t(NOT_AVAILABLE_ID),
  },
  {
    id: 'teamName',
    header: t('sg-column-team-name'),
    width: 160,
    minWidth: 80,
    sortingField: 'teamName',
    cell: (item) => item.teamName ?? t(NOT_AVAILABLE_ID),
  },
  {
    id: 'cxFamilyId',
    header: t('sg-column-cx-family'),
    width: 150,
    minWidth: 90,
    sortingComparator: (a, b) =>
      (a.latestVersion?.cxFamilyId ?? '').localeCompare(
        b.latestVersion?.cxFamilyId ?? '',
      ),
    cell: (item) => item.latestVersion?.cxFamilyId ?? t(NOT_AVAILABLE_ID),
  },
  {
    id: 'cxIds',
    header: t('sg-column-cx-ids'),
    width: 180,
    minWidth: 100,
    cell: (item) => (
      <ExpandableCell items={item.latestVersion?.cxIds} maxVisible={3} />
    ),
  },
  {
    id: 'sourceLocale',
    header: t('sg-column-source-locale'),
    width: 100,
    cell: (item) => item.latestVersion?.sourceLocale ?? t(NOT_AVAILABLE_ID),
  },
  {
    id: 'stringCount',
    header: t('sg-column-string-count'),
    width: 100,
    sortingComparator: (a, b) =>
      (a.latestVersion?.stringCount ?? 0) - (b.latestVersion?.stringCount ?? 0),
    cell: (item) => item.latestVersion?.stringCount ?? t(NOT_AVAILABLE_ID),
  },
  {
    id: 'createdAt',
    header: t('sg-column-created-on'),
    width: 180,
    minWidth: 120,
    sortingComparator: (a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0),
    cell: (item) => {
      // Date and author are ONE resource, not a date span beside a "by {author}"
      // span: the word order of "<date> by <author>" differs by language, and a
      // translator shown the fragment alone cannot move it.
      const date = formatEpochMillisDate(t, locale, item?.createdAt);
      return (
        <Box variant="span">
          {item?.createdBy
            ? t('namespace-metadata-date-by-author', {
                date,
                author: item.createdBy,
              })
            : date}
        </Box>
      );
    },
  },
  {
    id: 'updatedAt',
    header: t('sg-column-updated-on'),
    width: 180,
    minWidth: 120,
    sortingComparator: (a, b) =>
      (a.latestVersion?.updatedAt ?? 0) - (b.latestVersion?.updatedAt ?? 0),
    cell: (item) => {
      const date = formatEpochMillisDate(
        t,
        locale,
        item.latestVersion?.updatedAt,
      );
      return (
        <Box variant="span">
          {item.latestVersion?.updatedBy
            ? t('namespace-metadata-date-by-author', {
                date,
                author: item.latestVersion.updatedBy,
              })
            : date}
        </Box>
      );
    },
  },
];
